import { useState, useEffect, useRef } from "react";
import { Rocket, Loader, CheckCircle, ExternalLink, Play, Zap } from "lucide-react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PHASE_LABELS = {
  starting: "Iniciando pipeline...",
  research: "Investigando oportunidad (GPT-5.2)...",
  creating_product: "Creando producto digital (GPT-5.2)...",
  launching: "Lanzando marca + Wompi checkout...",
  live: "PRODUCTO EN VIVO",
};

export function PilotPanel({ onPilotComplete }) {
  const [running, setRunning] = useState(false);
  const [phase, setPhase] = useState("");
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const pollRef = useRef(null);

  // Cleanup polling on unmount
  useEffect(() => {
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, []);

  const pollStatus = (runId) => {
    pollRef.current = setInterval(async () => {
      try {
        const res = await axios.get(`${API}/pilot/run/${runId}`);
        const data = res.data;
        setPhase(PHASE_LABELS[data.phase] || data.phase);

        if (data.status === "complete") {
          clearInterval(pollRef.current);
          setResult(data.result);
          setRunning(false);
          if (onPilotComplete) onPilotComplete();
        } else if (data.status === "error") {
          clearInterval(pollRef.current);
          setError(data.error || "Error en pipeline");
          setRunning(false);
        }
      } catch (_) {}
    }, 3000);
  };

  const runPilot = async () => {
    setRunning(true);
    setError(null);
    setResult(null);
    setPhase("Iniciando pipeline...");

    try {
      const res = await axios.post(`${API}/pilot/run`);
      if (res.data.run_id) {
        pollStatus(res.data.run_id);
      } else {
        setError("No se obtuvo run_id");
        setRunning(false);
      }
    } catch (e) {
      setError(e.response?.data?.detail || e.message);
      setRunning(false);
    }
  };

  return (
    <div data-testid="pilot-panel" style={{ flex: 1, display: "flex", flexDirection: "column" }}>
      <div className="panel-header">
        <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
          <Rocket size={10} strokeWidth={1.5} style={{ color: "#007AFF" }} />
          <span className="panel-title">PLAN PILOTO</span>
        </div>
        {result && <span className="font-mono" style={{ fontSize: 9, color: "#34C759" }}>LIVE</span>}
      </div>

      {!running && !result && !error && (
        <div style={{
          display: "flex", flexDirection: "column", alignItems: "center",
          justifyContent: "center", flex: 1, gap: 10, padding: 12
        }}>
          <div className="font-mono" style={{
            fontSize: 9, color: "#a1a1aa", textAlign: "center", lineHeight: 1.6, maxWidth: 240
          }}>
            Ejecuta el pipeline: GPT-5.2 investiga, crea producto real y lo lanza con Wompi
          </div>
          <button
            data-testid="run-pilot-button"
            onClick={runPilot}
            style={{
              display: "flex", alignItems: "center", gap: 8,
              padding: "10px 20px",
              background: "rgba(0,122,255,0.1)",
              border: "1px solid rgba(0,122,255,0.3)",
              borderRadius: 2, cursor: "pointer",
              fontFamily: "'IBM Plex Mono', monospace", fontSize: 11,
              color: "#007AFF", letterSpacing: "0.05em"
            }}
          >
            <Play size={14} strokeWidth={1.5} />
            EJECUTAR PIPELINE
          </button>
          <div className="font-mono" style={{ fontSize: 8, color: "#52525b" }}>
            Research → Create → Launch
          </div>
        </div>
      )}

      {running && (
        <div style={{
          display: "flex", flexDirection: "column", alignItems: "center",
          justifyContent: "center", flex: 1, gap: 10
        }}>
          <Loader size={20} strokeWidth={1.5} style={{ color: "#007AFF", animation: "spin 1s linear infinite" }} />
          <div className="font-mono" style={{ fontSize: 10, color: "#a1a1aa", textAlign: "center", padding: "0 8px" }}>
            {phase}
          </div>
          <div className="font-mono" style={{ fontSize: 8, color: "#52525b" }}>
            Puede tomar 30-90 seg...
          </div>
          <style>{`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
        </div>
      )}

      {error && !running && (
        <div style={{ padding: 10 }}>
          <div style={{
            padding: 10, background: "rgba(255,59,48,0.08)",
            border: "1px solid rgba(255,59,48,0.2)", borderRadius: 2, marginBottom: 8
          }}>
            <div className="font-mono" style={{ fontSize: 9, color: "#FF3B30", wordBreak: "break-word" }}>
              {error}
            </div>
          </div>
          <button onClick={runPilot} style={{
            width: "100%", padding: "6px",
            background: "none", border: "1px solid rgba(255,255,255,0.1)",
            borderRadius: 2, cursor: "pointer",
            fontFamily: "'IBM Plex Mono'", fontSize: 9, color: "#a1a1aa"
          }}>
            REINTENTAR
          </button>
        </div>
      )}

      {result && (
        <div className="panel-body">
          <div style={{
            display: "flex", alignItems: "center", gap: 6,
            padding: "5px 8px", marginBottom: 6,
            background: "rgba(52,199,89,0.08)", border: "1px solid rgba(52,199,89,0.2)",
            borderRadius: 2
          }}>
            <CheckCircle size={11} style={{ color: "#34C759" }} />
            <span className="font-mono" style={{ fontSize: 9, color: "#34C759" }}>
              PRODUCTO EN VIVO
            </span>
          </div>

          <div style={{ marginBottom: 6 }}>
            <div className="font-mono" style={{ fontSize: 8, color: "#71717a", letterSpacing: "0.1em" }}>MARCA</div>
            <div style={{ fontSize: 13, fontWeight: 600, color: "#fff" }}>{result.brand_name}</div>
          </div>

          <div style={{ marginBottom: 6 }}>
            <div className="font-mono" style={{ fontSize: 8, color: "#71717a", letterSpacing: "0.1em" }}>PRODUCTO</div>
            <div style={{ fontSize: 11, color: "#d4d4d8" }}>{result.product_title}</div>
          </div>

          <div style={{
            display: "grid", gridTemplateColumns: "1fr 1fr", gap: 1,
            background: "rgba(255,255,255,0.04)", marginBottom: 6
          }}>
            <div style={{ padding: "5px 8px", background: "#09090b" }}>
              <div className="font-mono" style={{ fontSize: 8, color: "#71717a" }}>USD</div>
              <div className="font-mono" style={{ fontSize: 13, color: "#34C759", fontWeight: 600 }}>${result.price_usd}</div>
            </div>
            <div style={{ padding: "5px 8px", background: "#09090b" }}>
              <div className="font-mono" style={{ fontSize: 8, color: "#71717a" }}>COP</div>
              <div className="font-mono" style={{ fontSize: 13, color: "#34C759", fontWeight: 600 }}>${result.price_cop?.toLocaleString()}</div>
            </div>
          </div>

          <div style={{ display: "flex", flexDirection: "column", gap: 3 }}>
            <a href={result.product_url} target="_blank" rel="noopener noreferrer"
              data-testid="product-page-link" style={{
                display: "flex", alignItems: "center", gap: 6,
                padding: "5px 10px", background: "rgba(0,122,255,0.08)",
                border: "1px solid rgba(0,122,255,0.2)", borderRadius: 2,
                textDecoration: "none", fontFamily: "'IBM Plex Mono'", fontSize: 9, color: "#007AFF"
              }}>
              <ExternalLink size={10} /> VER LANDING PAGE
            </a>
            <a href={result.checkout_url} target="_blank" rel="noopener noreferrer"
              data-testid="checkout-link" style={{
                display: "flex", alignItems: "center", gap: 6,
                padding: "5px 10px", background: "rgba(52,199,89,0.08)",
                border: "1px solid rgba(52,199,89,0.2)", borderRadius: 2,
                textDecoration: "none", fontFamily: "'IBM Plex Mono'", fontSize: 9, color: "#34C759"
              }}>
              <Zap size={10} /> WOMPI CHECKOUT
            </a>
          </div>

          <button onClick={() => { setResult(null); setError(null); }}
            data-testid="run-another-pilot" style={{
              width: "100%", marginTop: 6, padding: "5px",
              background: "none", border: "1px solid rgba(255,255,255,0.08)",
              borderRadius: 2, cursor: "pointer",
              fontFamily: "'IBM Plex Mono'", fontSize: 8, color: "#71717a"
            }}>
            LANZAR OTRA MARCA
          </button>
        </div>
      )}
    </div>
  );
}
